// components/StreamCompositionView.js
import React from 'react';
import { COMPONENTS } from '../fluid-properties';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const StreamCompositionView = ({ stream }) => {
  if (!stream) return <div>No stream selected</div>;
  
  // Filter components with non-zero values and convert to percentage
  const compositionData = Object.entries(stream.composition)
    .filter(([_, value]) => value > 0.0001) // Filter out trace components
    .map(([component, value]) => ({
      name: component,
      percentage: value * 100
    }))
    .sort((a, b) => b.percentage - a.percentage); // Sort by percentage descending
  
  // Create data for stream properties table
  const streamProperties = [
    { property: 'Temperature', value: `${stream.temperature.toFixed(1)} °C` },
    { property: 'Pressure', value: `${stream.pressure.toFixed(1)} bar` },
    { property: 'Flow Rate', value: `${stream.flowRate.toFixed(1)} kmol/h` },
    { property: 'Mass Flow', value: `${stream.massFlow ? stream.massFlow.toFixed(1) : 'N/A'} t/h` },
    { property: 'Phase', value: stream.phase || 'Mixed' },
    { property: 'Molecular Weight', value: stream.properties ? stream.properties.MW.toFixed(2) : 'N/A' },
    { property: 'Z Factor', value: stream.properties ? stream.properties.Z.toFixed(3) : 'N/A' },
    { property: 'Density', value: stream.density ? `${stream.density.toFixed(1)} kg/m³` : 'N/A' },
  ];
  
  // Format chart data with component names and formulas
  const chartData = compositionData.map(item => {
    const component = Object.values(COMPONENTS).find(comp => comp.name === item.name);
    return {
      name: component ? component.name : item.name,
      formula: component ? component.formula : '',
      percentage: item.percentage
    };
  });
  
  return (
    <div className="stream-composition-view">
      <div className="composition-section">
        <h3>Composition Analysis</h3>
        <div className="composition-chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45} 
                textAnchor="end" 
                interval={0} 
                height={80}
                tick={{ fontSize: 12 }}
              />
              <YAxis label={{ value: 'Mol%', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                formatter={(value) => [`${value.toFixed(2)}%`, 'Mol%']}
                labelFormatter={(name) => {
                  const comp = chartData.find(item => item.name === name);
                  return `${name} ${comp?.formula ? `(${comp.formula})` : ''}`;
                }}
              />
              <Legend />
              <Bar dataKey="percentage" name="Component (mol%)" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="composition-tables">
          <div className="composition-table">
            <h4>Component Breakdown</h4>
            <table>
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Formula</th>
                  <th>Mol%</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.formula}</td>
                    <td>{item.percentage.toFixed(3)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="stream-properties">
            <h4>Stream Properties</h4>
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {streamProperties.map((prop, index) => (
                  <tr key={index}>
                    <td>{prop.property}</td>
                    <td>{prop.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="component-groups">
          <h4>Component Groups</h4>
          <div className="group-metrics">
            {renderComponentGroups(stream.composition)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate component groups
const renderComponentGroups = (composition) => {
  // Initialize groups
  const groups = {
    'C1': 0,
    'C2': 0,
    'C3': 0,
    'C4+': 0,
    'Inerts': 0,
    'Acid Gases': 0
  };
  
  // Calculate group percentages
  if (composition[COMPONENTS.METHANE.name]) {
    groups['C1'] += composition[COMPONENTS.METHANE.name] * 100;
  }
  
  if (composition[COMPONENTS.ETHANE.name]) {
    groups['C2'] += composition[COMPONENTS.ETHANE.name] * 100;
  }
  
  if (composition[COMPONENTS.PROPANE.name]) {
    groups['C3'] += composition[COMPONENTS.PROPANE.name] * 100;
  }
  
  // C4+ includes butane, pentane, hexane
  if (composition[COMPONENTS.BUTANE.name]) {
    groups['C4+'] += composition[COMPONENTS.BUTANE.name] * 100;
  }
  
  if (composition[COMPONENTS.PENTANE.name]) {
    groups['C4+'] += composition[COMPONENTS.PENTANE.name] * 100;
  }
  
  if (composition[COMPONENTS.HEXANE.name]) {
    groups['C4+'] += composition[COMPONENTS.HEXANE.name] * 100;
  }
  
  // Inerts include nitrogen
  if (composition[COMPONENTS.NITROGEN.name]) {
    groups['Inerts'] += composition[COMPONENTS.NITROGEN.name] * 100;
  }
  
  // Acid gases include CO2 and H2S
  if (composition[COMPONENTS.CO2.name]) {
    groups['Acid Gases'] += composition[COMPONENTS.CO2.name] * 100;
  }
  
  if (composition[COMPONENTS.H2S.name]) {
    groups['Acid Gases'] += composition[COMPONENTS.H2S.name] * 100;
  }
  
  // Convert to array for rendering
  return Object.entries(groups).map(([group, percentage]) => (
    <div className="group-metric" key={group}>
      <div className="group-name">{group}</div>
      <div className="group-bar">
        <div 
          className="group-bar-fill" 
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
        <span className="group-percentage">{percentage.toFixed(1)}%</span>
      </div>
    </div>
  ));
};

export default StreamCompositionView;